// Users management page
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useUser, usePermissions, useOrganization } from '@/lib/hooks'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { 
  Users as UsersIcon, 
  Mail, 
  UserPlus, 
  Shield, 
  Clock, 
  CheckCircle,
  XCircle,
  Copy,
  Gift
} from 'lucide-react'
import { format } from 'date-fns'
import EquityOfferForm from '@/components/custom-ui/equity-offer-form'
import { Database } from '@/database.types'

// Remove the custom interfaces and use database types
type User = Database['public']['Tables']['users']['Row'] & {
  roles: Database['public']['Tables']['roles']['Row'] | null
  project_role?: string
}

type Invitation = Database['public']['Tables']['invitations']['Row'] & {
  roles: Database['public']['Tables']['roles']['Row'] | null
  users: Database['public']['Tables']['users']['Row'] | null
}

export default function UsersPage() {
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('2') // Default to employee
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [showOfferDialog, setShowOfferDialog] = useState(false)
  const [selectedUserForOffer, setSelectedUserForOffer] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { profile } = useUser()
  const { currentProject } = useProject()
  const { currentOrganization } = useOrganization()
  const getToken = useGetToken()
  const permissions = usePermissions()


  // Fetch project members
  const { data: projectMembers, isLoading: usersLoading } = useQuery<any[]>({
    queryKey: ['project-members', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('project_members')
        .select(`
          *,
          users!inner (
            id,
            email,
            first_name,
            last_name,
            date_joined,
            role_id,
            roles (*)
          )
        `)
        .eq('project_id', currentProject.id)
        .order('joined_at', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  // Map project members to users format for compatibility
  const users = projectMembers?.map(pm => ({
    ...pm.users,
    project_role: pm.role // Add project-specific role
  }))

  // Debug logging
  console.log('Project Members Data:', projectMembers)
  console.log('Mapped Users:', users)

  // Fetch invitations
  const { data: invitations, isLoading: invitationsLoading } = useQuery({
    queryKey: ['invitations', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return []

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('invitations')
        .select(`
          *,
          roles:role_id (*),
          invited_by_user:invited_by (first_name, last_name)
        `)
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as any[] || []
    },
    enabled: permissions.canInviteUsers && !!currentOrganization
  })

  // Fetch available roles
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('roles')
        .select('*')
        .order('id')

      if (error) throw error
      return data || []
    }
  })

  // Send invitation mutation
  const { mutate: sendInvitation, isPending: isSending } = useMutation({
    mutationFn: async ({ email, role_id }: { email: string; role_id: number }) => {
      if (!currentOrganization) throw new Error('No organization selected')

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .rpc('invite_to_organization', {
          p_email: email,
          p_organization_id: currentOrganization.id,
          p_role_id: role_id
        })

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      setShowInviteDialog(false)
      setInviteEmail('')
      setInviteRole('2')
    }
  })

  const handleInvite = () => {
    if (!inviteEmail) return
    sendInvitation({ 
      email: inviteEmail, 
      role_id: parseInt(inviteRole) 
    })
  }

  const handleMakeOffer = (userId: string) => {
    setSelectedUserForOffer(userId)
    setShowOfferDialog(true)
  }

  const copyInviteLink = (token: string) => {
    const inviteUrl = `${window.location.origin}/invite/${token}`
    navigator.clipboard.writeText(inviteUrl)
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'expired':
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-600" />
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />
    }
  }

  const getRoleBadgeColor = (roleName: string | undefined) => {
    switch (roleName) {
      case 'founder':
        return 'bg-purple-100 text-purple-800'
      case 'admin':
        return 'bg-red-100 text-red-800'
      case 'owner':
        return 'bg-blue-100 text-blue-800'
      case 'member':
        return 'bg-green-100 text-green-800'
      case 'investor':
        return 'bg-yellow-100 text-yellow-800'
      case 'advisor':
        return 'bg-indigo-100 text-indigo-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Team Members</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage users and send invitations
          </p>
        </div>
        {permissions.canInviteUsers && (
          <Button onClick={() => setShowInviteDialog(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        )}
      </div>

      {/* Active Users */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <UsersIcon className="h-5 w-5 mr-2" />
            Active Users ({users?.length || 0})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usersLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center">
                    <div className="animate-pulse">Loading users...</div>
                  </td>
                </tr>
              ) : users?.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users?.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.project_role || user.roles?.name)}`}>
                        {user.project_role || user.roles?.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(user.date_joined), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {permissions.canInviteUsers && user.id !== profile?.id && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleMakeOffer(user.id)}
                            className="mr-2"
                            title="Make equity offer"
                          >
                            <Gift className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Change role">
                            <Shield className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invitations */}
      {permissions.canInviteUsers && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Pending Invitations
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invited By
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitationsLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center">
                      <div className="animate-pulse">Loading invitations...</div>
                    </td>
                  </tr>
                ) : invitations?.filter(inv => inv.status === 'pending').length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      No pending invitations
                    </td>
                  </tr>
                ) : (
                  invitations?.filter(inv => inv.status === 'pending').map((invitation) => (
                    <tr key={invitation.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invitation.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(invitation.roles?.name)}`}>
                          {invitation.roles?.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm">
                          {getStatusIcon(invitation.status)}
                          <span className="ml-2 text-gray-500">
                            Expires {format(new Date(invitation.expires_at), 'MMM d')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invitation.invited_by_user?.first_name} {invitation.invited_by_user?.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyInviteLink(invitation.token)}
                        >
                          {copiedToken === invitation.token ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your team. They'll receive a link to create their account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles?.map((role) => (
                    <SelectItem key={role.id} value={role.id.toString()}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteDialog(false)}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={isSending || !inviteEmail}>
              {isSending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equity Offer Dialog */}
      <EquityOfferForm
        open={showOfferDialog}
        onOpenChange={(open) => {
          setShowOfferDialog(open)
          if (!open) setSelectedUserForOffer(null)
        }}
        selectedUserId={selectedUserForOffer || undefined}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['equity-offers'] })
          setShowOfferDialog(false)
          setSelectedUserForOffer(null)
        }}
      />
    </div>
  )
}
